import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import { Box, MenuItem, type SelectChangeEvent } from '@mui/material';
import { LanguageType } from '../../../../@types';
import NeobetSelectField from './NeobetSelectField';
import { useNeobetLanguageSelect } from './useNeobetLanguageSelect';

const styles = {
  root: {
    justifyContent: 'flex-start',
  },
  control: {
    width: { xs: '100%', md: 190 },
    minWidth: 132,
    flexShrink: 0,
  },
  select: {
    minHeight: 36,
    '&&&.MuiInputBase-sizeSmall': {
      minHeight: 36,
    },
    '&& .MuiSelect-select': {
      minHeight: 36,
      boxSizing: 'border-box',
      py: 0,
      pl: 1.25,
      pr: 4,
      fontSize: 13,
      lineHeight: '18px',
      justifyContent: 'flex-start',
      textAlign: 'left',
    },
  },
  selectedValue: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 0.75,
    minWidth: 0,
    width: '100%',
    maxWidth: '100%',
  },
  globeIcon: {
    width: 16,
    height: 16,
    flexShrink: 0,
    color: 'neutral.100',
  },
  selectedLabel: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  menuItem: {
    typography: 'body3',
  },
  menuItemLabel: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  selectedIcon: {
    flexShrink: 0,
    fontSize: 18,
    color: 'primary.500',
  },
} as const;

const NeobetFooterLanguageSelect = () => {
  const { availableLanguages, handleLanguageChange, label, value } = useNeobetLanguageSelect();

  const handleChange = (event: SelectChangeEvent) => {
    handleLanguageChange(event.target.value as LanguageType);
  };

  return (
    <NeobetSelectField
      label={label}
      value={value}
      onChange={handleChange}
      renderValue={(selectedValue) => {
        const selectedLabel =
          availableLanguages.find((language) => language.key === selectedValue)?.label ?? selectedValue;

        return (
          <Box component='span' sx={styles.selectedValue}>
            <PublicRoundedIcon sx={styles.globeIcon} />
            <Box component='span' sx={styles.selectedLabel}>
              {selectedLabel}
            </Box>
          </Box>
        );
      }}
      inputProps={{
        id: 'neobet-footer-language-select-input',
        'aria-label': label,
      }}
      IconComponent={KeyboardArrowDownRoundedIcon}
      showLabel={false}
      testId='neobet-footer-language-select'
      rootSx={styles.root}
      controlSx={styles.control}
      selectSx={styles.select}
      selectDisplayProps={{
        style: {
          justifyContent: 'flex-start',
          textAlign: 'left',
        },
      }}
    >
      {availableLanguages.map((language) => {
        const isSelected = language.key === value;

        return (
          <MenuItem key={language.key} value={language.key} selected={isSelected} sx={styles.menuItem}>
            <Box component='span' sx={styles.menuItemLabel}>
              {language.label}
            </Box>
            {isSelected && <CheckRoundedIcon sx={styles.selectedIcon} />}
          </MenuItem>
        );
      })}
    </NeobetSelectField>
  );
};

export default NeobetFooterLanguageSelect;
